import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Modal,
  Input,
  Select,
  Switch,
  Tag,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import {
  createDevice,
  getDevices,
  updateDevice,
  deleteDevice,
} from "../api/devices/devicesRequest";
import { getCurrentDateTime } from "../utils/date";
import PermissionRender from "../components/PermissionRender";

const { Content } = Layout;
const { Option } = Select;

function Devices() {
  const [devices, setDevices] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState("");
  const [newDeviceId, setNewDeviceId] = useState("");
  const [newPlatform, setNewPlatform] = useState("android");
  const [isActive, setIsActive] = useState(true);
  const [editingDevice, setEditingDevice] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const data = await getDevices();

      setDevices(data);
    } catch (error) {
      message.error("Échec de la récupération des appareils.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = async () => {
    if (newDeviceName.trim() && newDeviceId.trim()) {
      try {
        await createDevice({
          device_name: newDeviceName,
          platform: newPlatform,
          device_id: newDeviceId,
          is_active: isActive,
        });

        setNewDeviceName("");
        setNewPlatform("android");
        setNewDeviceId("");
        setIsActive(true);
        setIsModalVisible(false);
        fetchDevices();
        message.success("Appareil ajouté avec succès");
      } catch (error) {
        message.error("Échec de l'ajout de l'appareil");
      }
    } else {
      message.error("Veuillez entrer un nom et un ID pour l'appareil");
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      await deleteDevice(deviceId);
      fetchDevices();
      message.success("Appareil supprimé avec succès");
    } catch (error) {
      message.error("Échec de la suppression de l'appareil");
    }
  };

  const showDeleteConfirm = (deviceId) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cet appareil ?",
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk() {
        handleDeleteDevice(deviceId);
      },
    });
  };

  const handleEditDevice = (device) => {
    setIsEditing(true);
    setEditingDevice(device);
    setNewDeviceName(device.device_name);
    setNewPlatform(device.platform);
    setNewDeviceId(device.device_id);
    setIsActive(device.is_active);
    setIsModalVisible(true);
  };

  const handleUpdateDevice = async () => {
    if (newDeviceName.trim()) {
      try {
        await updateDevice(editingDevice.id, {
          device_name: newDeviceName,
          platform: newPlatform,
          is_active: isActive,
        });

        setNewDeviceName("");
        setNewPlatform("android");
        setIsModalVisible(false);
        setIsEditing(false);
        fetchDevices();
        message.success("Appareil mis à jour avec succès");
      } catch (error) {
        message.error("Échec de la mise à jour de l'appareil.");
      }
    } else {
      message.error("Veuillez entrer un nom d'appareil");
    }
  };

  const resetForm = () => {
    setNewDeviceName("");
    setNewPlatform("android");
    setNewDeviceId("");
    setIsActive(true);
    setIsEditing(false);
  };

  const columns = [
    {
      title: "Nom de l'appareil",
      dataIndex: "device_name",
      key: "device_name",
    },
    {
      title: "Plateforme",
      dataIndex: "platform",
      key: "platform",
    },
    {
      title: "ID de l'appareil",
      dataIndex: "device_id",
      key: "device_id",
    },
    {
      title: "Agent actuel",
      dataIndex: ["agent", "full_name"],
      key: "agent",
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "Dernière mise à jour",
      dataIndex: "updated_at",
      key: "updated_at",
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (text, record) => (
        <Space size="middle">
          <PermissionRender requiredPermission={["devices", "update"]}>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditDevice(record)}
            >
              Modifier
            </Button>
          </PermissionRender>
          <PermissionRender requiredPermission={["devices", "delete"]}>
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => showDeleteConfirm(record.id)}
            >
              Supprimer
            </Button>
          </PermissionRender>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24}>
              <PermissionRender requiredPermission={["devices", "create"]}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsModalVisible(true)}
                  className="w-full"
                >
                  Créer un nouvel appareil
                </Button>
              </PermissionRender>
            </Col>
          </Row>
        </Card>

        <Card className="shadow-sm">
          <Table
            dataSource={devices.map((item, index) => ({ key: index, ...item }))}
            columns={columns}
            pagination={false}
            loading={loading}
          />
        </Card>

        <Modal
          title={isEditing ? "Modifier l'appareil" : "Ajouter un appareil"}
          visible={isModalVisible}
          onOk={isEditing ? handleUpdateDevice : handleAddDevice}
          onCancel={() => {
            setIsModalVisible(false);
            resetForm();
          }}
        >
          <div>
            <Input
              placeholder="Nom de l'appareil"
              value={newDeviceName}
              onChange={(e) => setNewDeviceName(e.target.value)}
            />
            <Input
              placeholder="ID de l'appareil"
              value={newDeviceId}
              onChange={(e) => setNewDeviceId(e.target.value)}
              style={{ marginTop: "1rem" }}
              disabled={isEditing}
            />

            <div className="mt-3 flex gap-4 items-center ">
              <span>Plateforme : </span>
              <Select
                value={newPlatform}
                onChange={(value) => setNewPlatform(value)}
                style={{ width: 120 }}
              >
                <Option value="android">Android</Option>
                <Option value="ios">iOS</Option>
              </Select>
            </div>
            <div className="mt-3 flex gap-4 items-center ">
              <span>Statut : </span>
              <Switch checked={isActive} onChange={setIsActive} />
            </div>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
}

export default Devices;
